// @flow
import moment from 'moment';
import * as React from 'react';
import { ResponsiveContainer, AreaChart, YAxis, XAxis, Tooltip, Area, CartesianGrid, ReferenceLine } from 'recharts';
import styled, { withTheme } from 'styled-components';
import { translate } from 'react-i18next';
import _ from 'lodash';
import { displayNumber } from 'common/components/Number';
import { isDevFlagEnabled } from 'common/components/DevFlag';
import Proposition from 'common/modules/proposition/domain/proposition';
import DateRange from '../../../domain/DateRange';

import type { MetricByDate } from '../domain/CampaignMetrics';
import CampaignMetricByDayTooltip from './CampaignMetricByDayTooltip';

const PERCENT_TO_SHOW_AFTER_MAX_IMPRESSIONS = 20;
const DAY = 'day';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
`;

type ChartEntry = {
  name: string;
  cumulative: ?number;
  disabledAt: ?string | ?number;
};

type Chart = Array<ChartEntry>;

type Props = {
  metricByDate?: MetricByDate,
  spotsByDate?: MetricByDate,
  proposition?: Proposition,
  primary: string,
  secondary: string,
  theme: Object,
  t: any,
  from: moment,
  to: moment,
  withTooltip?: boolean;
  withAnimation?: boolean;
};

type State = {
  chart: Chart,
};

class CumulativeCampaignMetricByDayLineChart extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const chart = this.convertMetricsToChart(props.metricByDate);
    this.state = {
      chart,
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    if (!_.isEqual(this.props.metricByDate, nextProps.metricByDate)) {
      this.setState({ chart: this.convertMetricsToChart(nextProps.metricByDate) });
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return !_.isEqual(this.state.chart, nextState.chart);
  }

  convertMomentToStandardDateString(date: moment): string {
    return this.props.t('date.standard', { value: date });
  }

  convertMetricsToChart(metricByDate: ?MetricByDate): Chart {
    const { from, to } = this.props;
    const metrics = metricByDate || {};

    const dates = Object.keys(metrics);

    dates.sort((a, b) => Number(a) - Number(b));

    const campaignDates = new DateRange({ from, to }).getAllDates();
    const max = this.getYAxisDomain(metricByDate)[1];
    const yesterday = this.yesterday();

    const entries = campaignDates.map(date => ({
      name: this.convertMomentToStandardDateString(date),
      cumulative: date.isSameOrBefore(yesterday, DAY) ? metrics[Number(date.unix())] || 0 : null,
      disabledAt: date.isSameOrAfter(yesterday, DAY) ? max : null,
    }));

    return entries;
  }

  yesterday(): moment {
    return moment().subtract(1, DAY);
  }

  getBiggestImpressionsCount(metricByDate: ?MetricByDate): number {
    if (!metricByDate) {
      return 0;
    }

    const keys = Object.keys(metricByDate).map(Number);
    const lastCumulative = keys[keys.length - 1];

    return metricByDate[lastCumulative];
  }

  getYAxisDomain = (metricByDate: ?MetricByDate): (string | number)[] => {
    let maxY = this.getBiggestImpressionsCount(metricByDate) || 0;
    if (this.props.proposition) {
      const estimatedImpressions = this.props.proposition.getTotalImpressions() || 0;
      maxY = Math.max(estimatedImpressions, maxY);
    }

    maxY *= (1 + (PERCENT_TO_SHOW_AFTER_MAX_IMPRESSIONS / 100));
    return [0, Math.floor(maxY)];
  }

  render() {
    const { theme: { colors, fonts }, t, proposition, metricByDate, withTooltip, withAnimation, to, from } = this.props;
    const estimatedImpressions = proposition ? proposition.getTotalImpressions() : 0;
    const today = moment();
    return (
      <Wrapper>
        {this.state.chart.length === 0 &&
          <div style={fonts.style.cardTitle}>{t('noDataAvailable')}</div>
        }
        {this.state.chart.length > 0 &&
          <ResponsiveContainer>
            <AreaChart data={this.state.chart}>
              <CartesianGrid stroke={colors.graph.lines} strokeDasharray="5 5" vertical={false} />
              <Area
                fill={colors.primary}
                key={'name'}
                dataKey="cumulative"
                stroke={this.props.primary}
                isAnimationActive={!!withAnimation}
              />
              <Area
                strokeWidth={0}
                fill={colors.graph.disabled}
                key={'max'}
                dataKey="disabledAt"
                isAnimationActive={!!withAnimation}
              />
              <XAxis
                strokeWidth="4"
                fontSize={fonts.style.sub.fontSize}
                tickLine
                interval="preserveStart"
                tick={{ transform: 'translate(0, 15)', ...fonts.style.xsmall }}
                dataKey="name"
              />
              <YAxis
                strokeWidth="4"
                fontSize={fonts.style.sub.fontSize}
                tickLine={false}
                tick={{ transform: 'translate(-10, 0)', ...fonts.style.small }}
                scale="linear"
                domain={this.getYAxisDomain(metricByDate)}
              />
              { withTooltip &&
                <Tooltip
                  entryValueLabel={t('cumulative')}
                  content={
                    <CampaignMetricByDayTooltip
                      data={this.props.spotsByDate}
                    />
                  }
                />
              }
              { today.isBetween(from, to, DAY, '(]') &&
                <ReferenceLine
                  x={this.convertMomentToStandardDateString(this.yesterday())}
                  stroke={colors.graph.verticalReferenceLine}
                  strokeDasharray="5"
                  strokeWidth={2}
                />
              }
              { isDevFlagEnabled('TARGET_LINE') && proposition &&
                <ReferenceLine
                  ifOverlow="extendDomain"
                  y={estimatedImpressions}
                  label={{ value: t('estimationTarget', { value: displayNumber(estimatedImpressions) }), position: 'insideBottomRight', fill: colors.accent }}
                  stroke={colors.accent}
                  strokeWidth={3}
                />
              }
            </AreaChart>
          </ResponsiveContainer>
        }
      </Wrapper>
    );
  }
}

export default withTheme(translate('commonCampaign')(CumulativeCampaignMetricByDayLineChart));



// WEBPACK FOOTER //
// ./src/common/modules/campaign/components/CumulativeCampaignMetricByDayLineChart.js